import { useState } from "react";

import { authoring as authoringApi } from "../api/resources";
import { ErrorBanner, Field, Modal } from "./ui";
import type { LibraryQuestion } from "../lib/types";

const TYPES = [
  "text",
  "long_text",
  "number",
  "date",
  "boolean",
  "single_select",
  "multi_select",
  "matrix",
  "file",
  "panel_dynamic",
  "rating",
  "ranking",
  "html",
];

/**
 * Edit or add a library question.
 *
 * The semantic ID is shown but not editable on an existing question: it is the permanent address
 * every answer, evidence row and fact points at. The form says so rather than silently dropping
 * the field, because the rule is the thing worth teaching.
 */
export function QuestionEditor({
  moduleKey,
  question,
  pages,
  onClose,
  onSaved,
}: {
  moduleKey: string;
  question?: LibraryQuestion;
  pages: Array<{ key: string; title: string }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = Boolean(question);
  const [form, setForm] = useState({
    semantic_id: question?.semantic_id ?? "",
    type: question?.question_type_code ?? "text",
    title: question?.title ?? "",
    description: question?.description ?? "",
    placeholder: question?.placeholder ?? "",
    page_key: question?.page_key ?? (pages[0]?.key ?? ""),
    is_required: question?.is_required ?? false,
    extractable: question?.extractable ?? true,
    choices: (question?.choices ?? []).map((choice) => choice.text ?? choice.value).join("\n"),
    why: String(question?.meta?.why ?? ""),
    reason: "",
  });
  const [error, setError] = useState<string | undefined>();
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  const needsChoices = form.type === "single_select" || form.type === "multi_select";

  async function save() {
    setBusy(true);
    setError(undefined);
    setErrors([]);

    const choices = form.choices
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => ({ value: line.toLowerCase().replace(/[^a-z0-9]+/g, "_"), text: line }));

    const meta = { ...(question?.meta ?? {}) };
    if (form.why) meta.why = form.why;
    else delete meta.why;

    try {
      if (editing) {
        await authoringApi.updateQuestion(moduleKey, question!.semantic_id, {
          title: form.title,
          description: form.description || null,
          placeholder: form.placeholder || null,
          page_key: form.page_key,
          is_required: form.is_required,
          extractable: form.extractable,
          ...(needsChoices ? { choices } : {}),
          meta,
          reason: form.reason || undefined,
        });
      } else {
        await authoringApi.addQuestion(moduleKey, {
          semantic_id: form.semantic_id,
          type: form.type,
          title: form.title,
          description: form.description || undefined,
          placeholder: form.placeholder || undefined,
          page_key: form.page_key,
          is_required: form.is_required,
          extractable: form.extractable,
          ...(needsChoices ? { choices } : {}),
          meta,
          reason: form.reason || undefined,
        });
      }
      onSaved();
    } catch (cause) {
      const detail = (cause as { detail?: { errors?: string[] } }).detail;
      if (detail?.errors) setErrors(detail.errors);
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={editing ? `Edit ${question!.semantic_id}` : "Add a question"}
      onClose={onClose}
      footer={
        <>
          <button className="btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="primary"
            disabled={busy || !form.title || (!editing && !form.semantic_id)}
            onClick={() => void save()}
          >
            {busy ? "Saving…" : editing ? "Save changes" : "Add question"}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} />
      {errors.length ? (
        <div className="banner banner--error">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {errors.map((message) => (
              <li key={message} className="small">
                {message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Field
        label="Semantic ID"
        hint={
          editing
            ? "Permanent. Every answer and fact already collected points at it — retire this question and add a new one instead of renaming."
            : "A meaningful, stable address such as warehouse.inventory_source. Never a position like question_7."
        }
      >
        <input
          className="mono"
          value={form.semantic_id}
          disabled={editing}
          placeholder="warehouse.inventory_source"
          onChange={(event) => set("semantic_id", event.target.value)}
        />
      </Field>

      <div className="grid grid--two">
        <Field label="Type">
          <select
            value={form.type}
            disabled={editing && (question?.response_count ?? 0) > 0}
            onChange={(event) => set("type", event.target.value)}
          >
            {TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Page">
          <select value={form.page_key} onChange={(event) => set("page_key", event.target.value)}>
            {pages.map((page) => (
              <option key={page.key} value={page.key}>
                {page.title}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Question" hint="Ask it the way you would out loud.">
        <textarea
          rows={2}
          value={form.title}
          onChange={(event) => set("title", event.target.value)}
        />
      </Field>

      <Field label="Help text" hint="An example answer does more than a definition.">
        <textarea
          rows={2}
          value={form.description}
          onChange={(event) => set("description", event.target.value)}
        />
      </Field>

      <Field label="Why we ask" hint="Shown to the respondent. People answer better when they know.">
        <input value={form.why} onChange={(event) => set("why", event.target.value)} />
      </Field>

      {needsChoices ? (
        <Field label="Choices" hint="One per line.">
          <textarea
            rows={4}
            value={form.choices}
            onChange={(event) => set("choices", event.target.value)}
          />
        </Field>
      ) : null}

      <div className="row">
        <label className="row" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={form.is_required}
            onChange={(event) => set("is_required", event.target.checked)}
          />
          Required
        </label>
        <label className="row" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={form.extractable}
            onChange={(event) => set("extractable", event.target.checked)}
          />
          Extract into the knowledge model
        </label>
      </div>

      <Field label="Why are you changing this?" hint="Recorded in the question audit trail.">
        <input value={form.reason} onChange={(event) => set("reason", event.target.value)} />
      </Field>

      {editing && (question?.response_count ?? 0) > 0 ? (
        <p className="tiny faint" style={{ marginBottom: 0 }}>
          {question!.response_count} answer(s) already point at this question. Wording can change
          freely; the type cannot, because those answers would stop making sense.
        </p>
      ) : null}
    </Modal>
  );
}
